import { ReactNode, useCallback, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { AxiosError, AxiosResponse } from 'axios';

import { nameMatch, request } from 'app/utils';
import { Loading, Page } from 'components';
import {
  // PaymentsHeader,
  PaymentsLogin,
  // PaymentsSafeBuyFooter,
  // PaymentsSidebar,
  useOffers,
  BuyInformationResponse,
  OfferImageLayoutEnum,
} from 'features/offers';
import { useSchool } from 'features/school';
import { useAuth } from 'features/auth';
import { BiggerTop, BigTop, BigTopLeftRight, BigTopRight, NoImage, SmallTop } from 'features/offers/layouts';
import { fakeRecurrences } from 'features/offers/recurrences';
import { useTracking } from 'features/tracking';
import { useResources } from 'features/resources';
// import { mdiAlertRemove } from '@mdi/js';

interface MainProps {
  children: ReactNode;
}

const Main = ({ children }: MainProps) => {
  const { loginChecked } = useAuth();
  const { eventTracker } = useTracking();

  const { offerIdentifier } = useParams();

  const { apiGatewayUrl, schoolAlias, defaultAppUrl, schoolMelver } = useSchool();
  const { checkout, setCheckout, setBuyInformation, buyInformation, getCaptcha } = useOffers(); // TODO: setActiveCampaingIds
  const {
    schools: { schoolDiarioJaque, schoolTerraInvestimentos, schoolIsSquad },
  } = useResources();

  const checkIsRecurrence = useCallback(
    ({ offerName }: BuyInformationResponse) => {
      const isRecurrenceFake = fakeRecurrences.includes(offerIdentifier);

      if (isRecurrenceFake) return true;

      const isMelverCertifica = nameMatch(offerName, ['melver', 'certifica']);
      // const isMelverPro = ['melver', 'pro'].some((el) => offerName.toLowerCase().split(' ').includes(el));

      if (isMelverCertifica) return true;

      return false;
    },
    [offerIdentifier]
  );

  const prepare = useCallback(async () => {
    await request({
      url: `/api/v1/OfferPayment/BuyInformation/${offerIdentifier}`,
      method: 'get',
      baseURL: apiGatewayUrl,
      params: {
        schoolAlias,
      },
    })
      .then((response: AxiosResponse<BuyInformationResponse>) => {
        const {
          freeOffer,
          creditCardValues,
          boletoValues,
          pixValues,
          withAddress,
          withBirthDate,
          withEMailConfirmation,
          withPhone,
          withTaxId,
          offerName,
          disableCaptcha,
        } = response.data;

        const isRecurrence = checkIsRecurrence(response.data);
        let spotValue = 0;

        setBuyInformation({
          ...response.data,
          isRecurrence,
        });

        setCheckout({
          withAddress,
          withBirthDate,
          withEMailConfirmation,
          withPhone,
          withTaxId,
          disableCaptcha,
        });

        // if (productSlug === 'primeira-opcao') {
        //   setActiveCampaingIds({
        //     addContactToList: 191,
        //     addTagOnSuccessCreditCard: 168,
        //     addTagOnSuccessBoleto: 169,
        //     addTagOnSuccessPix: 170,
        //   });
        // } else if (productSlug === 'ap-p') {
        //   setActiveCampaingIds({
        //     addContactToList: 194,
        //     addContactToListOnSuccess: 195,
        //     addTagOnSuccessCreditCard: 177,
        //     addTagOnSuccessBoleto: 178,
        //     addTagOnSuccessPix: 184,
        //   });
        // }

        // let showValue = false;

        if (freeOffer) {
          setCheckout({
            paymentMethod: 'free',
            installments: 1,
            value: 0,
          });
        } else if (creditCardValues.length) {
          const installmens = creditCardValues[creditCardValues.length - 1];

          spotValue = creditCardValues[0]?.totalValue;

          // showValue = true;

          setCheckout({
            paymentMethod: 'creditCard',
            installments: installmens.installments,
            value: installmens.totalValue,
          });
        } else if (boletoValues.length) {
          const installmens = boletoValues[0];

          spotValue = boletoValues[0]?.totalValue;

          setCheckout({
            paymentMethod: 'boleto',
            installments: installmens.installments,
            value: installmens.totalValue,
          });
        } else if (pixValues.length) {
          const installmens = pixValues[0];

          spotValue = pixValues[0]?.totalValue;

          setCheckout({
            paymentMethod: 'pix',
            installments: installmens.installments,
            value: installmens.totalValue,
          });
        }

        setCheckout({
          paymentStep: 'payment',
          spotValue,
        });

        if ((window as any).fbq) {
          if (schoolMelver) {
            const isMelverCertifica = ['melver', 'certifica'].some((el) => offerName.toLowerCase().split(' ').includes(el));
            const isEtfsNasdaq = ['etfs', 'nasdaq'].some((el) => offerName.toLowerCase().split(' ').includes(el));
            const isDividendosAtivos = ['dividendos', 'ativos'].some((el) => offerName.toLowerCase().split(' ').includes(el));

            const isPlanoFormacaoAssessoresXP = nameMatch(offerName, ['plano', 'formação', 'assessores', 'xp']);

            const isAnalisePoliticaPlanoBasic = nameMatch(offerName, ['análise', 'política', 'plano', 'basic']);
            const isAnalisePoliticaPlanoPremium = nameMatch(offerName, ['análise', 'política', 'plano', 'premium']);

            const isOMosaico = nameMatch(offerName, ['o', 'mosaico']);

            const isDaTeoriaAoLucro = nameMatch(offerName, ['da', 'teoria', 'ao', 'lucro']);

            const isMBAMercadoFinanceiro = nameMatch(offerName, ['mba', 'mercado', 'financeiro']);

            const isFormacaoEmOpcoes = nameMatch(offerName, ['formação', 'em', 'opções']);

            const isMBAFormacaoBroker = nameMatch(offerName, ['mba', 'formação', 'broker']);

            const isPreparatorioCea = nameMatch(offerName, ['preparatório', 'cea']);
            const isCPA10 = ['cpa', '10'].filter((el) => offerName.toLowerCase().split('-').includes(el))?.length === 2;
            const isCPA20 = ['cpa', '20'].filter((el) => offerName.toLowerCase().split('-').includes(el))?.length === 2;

            if (isMelverCertifica) {
              (window as any).fbq('init', '724293966034794');
            } else if (isEtfsNasdaq) {
              (window as any).fbq('init', '732040421927282');
            } else if (isDividendosAtivos) {
              (window as any).fbq('init', '1646085369241982');
              // } else if (isCPA10) {
              //   (window as any).fbq('init', '573057464895958');
              // } else if (isCPA20) {
              //   (window as any).fbq('init', '1760937077697540');
            } else if (isPlanoFormacaoAssessoresXP) {
              (window as any).fbq('init', '2350131211895663');
            } else if (isOMosaico) {
              (window as any).fbq('init', '1508939923354700');
            } else if (isAnalisePoliticaPlanoBasic) {
              (window as any).fbq('init', '1646085369241982');
            } else if (isAnalisePoliticaPlanoPremium) {
              (window as any).fbq('init', '1646085369241982');
            } else if (isDaTeoriaAoLucro) {
              (window as any).fbq('init', '1891820744591346');
            } else if (isMBAMercadoFinanceiro) {
              (window as any).fbq('init', '1220029222355176');
            } else if (isFormacaoEmOpcoes) {
              (window as any).fbq('init', '1646085369241982');
              (window as any).fbq('init', '658202438820630');
            } else if (isMBAFormacaoBroker) {
              (window as any).fbq('init', '1646085369241982');
            } else if (isPreparatorioCea || isCPA10 || isCPA20) {
              (window as any).fbq('init', '724293966034794');
            } else {
              (window as any).fbq('init', '658202438820630');
            }

            (window as any).fbq('track', 'PageView');
          } else if (schoolDiarioJaque) {
            const isDiarioJaque = nameMatch(offerName, ['diario', 'da', 'jaque']);
            const isDiarioJaqueAcento = nameMatch(offerName, ['diário', 'da', 'jaque']);

            if (isDiarioJaque || isDiarioJaqueAcento) {
              (window as any).fbq('init', '1490119448269467');
            }
          } else if (schoolTerraInvestimentos) {
            const oDesafioRendaFixaExpress = nameMatch(offerName, ['o', 'desafio', 'renda', 'fixa', 'express']);
            const salaBlindadaArsenalTrader = nameMatch(offerName, ['sala', 'blindada', 'arsenal', 'trader']);
            const doZeroaTraderPRO = nameMatch(offerName, ['do', 'zero', 'a', 'trader', 'pro']);

            const encontroDeTradersLote2 = nameMatch(offerName, ['encontro', 'de', 'traders', 'lote', '2']);

            if (oDesafioRendaFixaExpress || salaBlindadaArsenalTrader || doZeroaTraderPRO) {
              (window as any).fbq('init', '3745243295727637');
            } else if (encontroDeTradersLote2) {
              (window as any).fbq('init', '316362183265758');
            }
          } else if (schoolIsSquad) {
            const preparatorioAncordSquad = nameMatch(offerName, ['preparatório', 'ancord', 'squad', 'investimentos']);

            if (preparatorioAncordSquad) {
              (window as any).fbq('init', '963219937788716');
            }

            (window as any).fbq('track', 'PageView');
          }

          setTimeout(() => {
            (window as any).fbq('track', 'InitiateCheckout', {
              content_name: offerName,
              content_ids: [offerIdentifier],
            });
          }, 100);
        }

        eventTracker(
          {
            category: 'checkout',
            action: 'Initiate Checkout',
            value: spotValue,
            product_name: offerName,
            schoolAlias,
          },
          true
        );

        // if (!showValue) {
        //   navigate(createUrl('productDetails', [productSlug]));
        // }
      })
      .catch((error: AxiosError) => {
        if (error?.response?.status) {
          window.location.href = `https://${defaultAppUrl}`;
        }
      });
    // .catch(() => {
    //   // navigate(createUrl('productDetails', [productSlug]));
    //   // window.location.href = 'https://melver.com.br';
    // })
    // .finally(() => {

    // });
  }, [offerIdentifier]);

  useEffect(() => {
    prepare();

    if (offerIdentifier) {
      getCaptcha(offerIdentifier);
    }
  }, [offerIdentifier]);

  if (checkout.paymentStep === 'loading' || !loginChecked) {
    return <Loading label="Por favor, aguarde." />;
  }

  let PaymentLayout = NoImage;

  if (buyInformation?.offerImageLayout === OfferImageLayoutEnum.BigTopRight) {
    PaymentLayout = BigTopRight;
  } else if (buyInformation?.offerImageLayout === OfferImageLayoutEnum.BigTopLeftRight) {
    PaymentLayout = BigTopLeftRight;
  } else if (buyInformation?.offerImageLayout === OfferImageLayoutEnum.BigTop) {
    PaymentLayout = BigTop;
  } else if (buyInformation?.offerImageLayout === OfferImageLayoutEnum.SmallTop) {
    PaymentLayout = SmallTop;
  } else if (buyInformation?.offerImageLayout === OfferImageLayoutEnum.BiggerTop) {
    PaymentLayout = BiggerTop;
  }

  return (
    <Page title={buyInformation ? `${buyInformation.offerName}` : ''}>
      <PaymentsLogin />

      <PaymentLayout>{children}</PaymentLayout>
    </Page>
  );
};

export default Main;
